import type { AnyObject } from 'antd/es/_util/type'

export const transformEmptyValuesToNull = (values: AnyObject) => {
  return Object.keys(values).reduce((result, key) => {
    const value = values[key]

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = transformEmptyValuesToNull(value)
    } else if (value === '' || value === undefined || value === null) {
      result[key] = null
    } else {
      result[key] = value
    }

    return result
  }, {} as AnyObject)
}

export const removeEmptyValues = (obj: AnyObject): AnyObject =>
  Object.keys(obj)
    .filter((i) => {
      const value = obj[i]
      if (
        !Array.isArray(value) &&
        typeof value === 'object' &&
        value !== null &&
        Object.keys(value).length === 0
      )
        return false

      return value !== null && value !== undefined && value !== ''
    })
    .reduce((acc, current) => ({ ...acc, [current]: obj[current] }), {})

export const phoneNumberRegex = /^[0-9]+$/

export const getStepForFieldError = (
  error: unknown,
  config: Record<string, number>
): number | null => {
  let errorData: unknown = undefined

  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response: unknown }).response === 'object' &&
    (error as { response: unknown }).response !== null &&
    'data' in (error as { response: { data: unknown } }).response
  ) {
    errorData = (error as { response: { data: unknown } }).response.data
  } else if (typeof error === 'object' && error !== null && 'body' in error) {
    errorData = (error as { body: unknown }).body
  }

  if (!errorData || typeof errorData !== 'object') {
    return null
  }

  const flattenErrors = (
    obj: Record<string, unknown>,
    prefix = ''
  ): Record<string, string[]> => {
    const flattened: Record<string, string[]> = {}

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key

      if (Array.isArray(value)) {
        const messages = value.filter(
          (msg): msg is string => typeof msg === 'string'
        )
        if (messages.length > 0) {
          flattened[fullKey] = messages
        }

        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            const nested = flattenErrors(
              item as Record<string, unknown>,
              `${fullKey}[${index}]`
            )
            Object.assign(flattened, nested)
          }
        })
      } else if (typeof value === 'string') {
        flattened[fullKey] = [value]
      } else if (typeof value === 'object' && value !== null) {
        const nested = flattenErrors(value as Record<string, unknown>, fullKey)
        Object.assign(flattened, nested)
      }
    }

    return flattened
  }

  const flattenedErrors = flattenErrors(errorData as Record<string, unknown>)

  const errorFields = Object.keys(flattenedErrors)
  const stepOrder = [0, 1, 2]

  for (const step of stepOrder) {
    for (const field of errorFields) {
      if (config[field] === step) {
        return step
      }

      const matchingKey = Object.keys(config).find((key) => {
        if (key.includes('.')) {
          return field.startsWith(key + '.') || field.startsWith(key + '[')
        } else {
          return (
            field === key ||
            field.startsWith(`${key}.`) ||
            field.startsWith(`${key}[`)
          )
        }
      })

      if (matchingKey && config[matchingKey] === step) {
        return step
      }
    }
  }

  return null
}
